class BundlePopup extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.selectedProducts = new Map();
    this.userSelections = new Map(); // Stockage persistant par productId: { productId, variantId, quantity, price }
    this.currentProductId = null;
    this.cachedResults = {};
    this.abortController = new AbortController();
    this.cartItems = [];
    this.totalCartQuantity = 0;
    this.translations = {};

    this.bindEvents();
    this.loadTranslations();
  }

  loadTranslations() {
    // Charger les traductions depuis le script JSON
    const translationsScript = this.querySelector('[data-bundle-translations]');
    if (translationsScript) {
      try {
        this.translations = JSON.parse(translationsScript.textContent);
      } catch (error) {
        console.error('Bundle Popup: Erreur lors du chargement des traductions:', error);
      }
    }
  }

  connectedCallback() {
    this.setupEventListeners();
    this.initializeScrollbar();
    // Initialiser de manière asynchrone pour charger l'état du panier
    this.initializeProgress();
  }

  bindEvents() {
    this.handleClose = this.handleClose.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.handleAddProduct = this.handleAddProduct.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleSkip = this.handleSkip.bind(this);
  }

  setupEventListeners() {
    // Initialiser la référence au cart pour les sections
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    // Close button
    const closeBtn = this.querySelector('.bundle-popup__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', this.handleClose);
    }

    // Overlay click
    const overlay = this.querySelector('.bundle-popup__overlay');
    if (overlay) {
      overlay.addEventListener('click', this.handleOverlayClick);
    }

    // Search input
    const searchInput = this.querySelector('.bundle-popup__search-input');
    if (searchInput) {
      searchInput.addEventListener('input', this.handleSearch);

      // Debounced search pour éviter trop de requêtes
      this.debouncedSearch = this.debounce(this.performPredictiveSearch.bind(this), 300);
    }

    // Action buttons
    const addButton = this.querySelector('.bundle-popup__add-button');
    const skipButton = this.querySelector('.bundle-popup__skip-button');

    if (addButton) {
      addButton.addEventListener('click', this.handleAddToCart);
    }

    if (skipButton) {
      skipButton.addEventListener('click', this.handleSkip);
    }

    // Suppression de l'ouverture automatique de la popup à l'ajout d'un produit
    subscribe(PUB_SUB_EVENTS.cartUpdate, async (data) => {
      console.log('Cart updated:', data);
      // Mettre à jour les données du panier à chaque événement cart:updated
      await this.loadCartState();
    });

    // Chargement instantané si demandé
    this.handleInstantLoad();

    // Keyboard events
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Click outside variant picker to close
    document.addEventListener('click', this.handleDocumentClick.bind(this));
  }

  async handleInstantLoad() {
    // Suppression de l'ouverture automatique de la popup au chargement
    if (this.dataset.loadInstantly === 'true') {
      await this.loadCartState();
    }
  }

  async showForProduct(productId) {
    this.currentProductId = productId;
    // Charger l'état du panier dès le déclenchement initial
    await this.loadCartState();
    await this.loadBundleRecommendations(productId);
  }

  async loadBundleRecommendations(productId) {
    // Suivre le modèle ProductRecommendations
    console.log('dataurl', this.dataset.url);
    try {
      const response = await fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`);
      const text = await response.text();

      const html = document.createElement('div');
      html.innerHTML = text;
      const recommendations = html.querySelector(`bundle-popup`);

      if (recommendations?.innerHTML.trim().length) {
        // Extraire seulement la liste des produits
        const productsList = recommendations.querySelector('.bundle-popup__products-list');
        if (productsList) {
          const currentProductsList = this.querySelector('.bundle-popup__products-list');
          if (currentProductsList) {
            currentProductsList.innerHTML = productsList.innerHTML;
            // Filtrer les produits pack après insertion
            //this.filterPackProducts(currentProductsList);
            this.setupProductEventListeners();

            // Récupérer l'état du panier avant d'ouvrir
            await this.loadCartState();

            // Synchroniser avec le panier d'abord
            this.syncProductsWithCart();

            // Puis restaurer les sélections utilisateur (priorité sur le panier)
            this.restoreUserSelections();

            this.updateProgress();
            if (this.dataset.loadInstantly !== 'true') {
              this.open();
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading bundle popup:', error);
    }
  }

  filterPackProducts(productsContainer) {
    // Filtrer les produits de type "pack" côté front
    if (!productsContainer) return;

    const products = productsContainer.querySelectorAll('.bundle-popup__product');
    let displayedCount = 0;
    const maxProducts = 10;

    products.forEach((product) => {
      // Vérifier si le produit est de type "pack" via les données ou le contenu
      const productElement = product.closest('[data-product-type]');
      const isPackProduct = productElement && productElement.dataset.productIsBundle === 'true';

      if (isPackProduct || titleContainsPack) {
        // Cacher le produit pack
        product.style.display = 'none';
      } else if (displayedCount < maxProducts) {
        // Afficher le produit non-pack
        product.style.display = '';
        displayedCount++;
      } else {
        // Cacher les produits au-delà de la limite
        product.style.display = 'none';
      }
    });

    console.log(`Bundle Popup: ${displayedCount} produits non-pack affichés sur ${products.length} total`);
  }

  setupProductEventListeners() {
    // Quantity buttons
    this.querySelectorAll('.bundle-popup__quantity-btn').forEach((btn) => {
      btn.addEventListener('click', this.handleQuantityChange);
    });

    // Add product buttons
    this.querySelectorAll('.bundle-popup__add-product-btn').forEach((btn) => {
      btn.addEventListener('click', this.handleAddProduct);
    });

    // Variant picker buttons
    this.querySelectorAll('.js-variant-picker input[type="radio"]').forEach((input) => {
      input.addEventListener('change', this.handleVariantChange.bind(this));
    });
  }

  handleDocumentClick(event) {
    if (!this.isOpen) return;

    // Vérifier si le clic est à l'intérieur d'un picker de variantes ouvert
    const openDetails = this.querySelectorAll('.js-variant-picker details[open]');

    openDetails.forEach((details) => {
      const isClickInside = details.contains(event.target);

      if (!isClickInside) {
        // Fermer le details si le clic est en dehors
        details.open = false;
        console.log('Bundle Popup: Variant picker fermé par clic extérieur');
      }
    });
  }

  handleVariantChange(event) {
    const input = event.target;
    const product = input.closest('.bundle-popup__product');
    const variantId = input.value;
    const variantPrice = input.dataset.variantPrice;
    const variantCompareAtPrice = input.dataset.variantCompareAtPrice;
    const variantReduction = input.dataset.variantReduction;
    const variantAvailable = input.dataset.variantAvailable === 'true';

    // Récupérer l'ancienne variante AVANT de mettre à jour
    const productId = product.dataset.productId;
    const oldVariantId = product.dataset.variantId;

    // Mettre à jour les données du produit
    product.dataset.variantId = variantId;
    product.dataset.price = variantPrice;

    // Mettre à jour le prix affiché dans la carte produit
    const priceContainer = product.querySelector('[data-product-price]');
    if (priceContainer) {
      const priceSpan = priceContainer.querySelector('.price__current');
      if (priceSpan) {
        priceSpan.textContent = this.formatPrice(parseInt(variantPrice));
      }
    }

    // Mettre à jour le texte sélectionné dans le summary
    const selectedText = product.querySelector('[data-selected-value]');
    if (selectedText) {
      const optionTitle = input.closest('label').querySelector('.variant-picker-container__modal-body--option-title');
      if (optionTitle) {
        selectedText.textContent = optionTitle.textContent;
      }
    }

    // Mettre à jour le prix dans le summary du variant picker
    const summaryPriceContainer = product.querySelector('.variant-picker-container__selected-variant--price');
    if (summaryPriceContainer) {
      let priceHTML = '';

      // Prix barré si compare_at_price existe
      if (variantCompareAtPrice && parseInt(variantCompareAtPrice) > parseInt(variantPrice)) {
        priceHTML += `<s>${this.formatPrice(parseInt(variantCompareAtPrice))}</s>`;
      }

      // Prix actuel
      priceHTML += `<span>${this.formatPrice(parseInt(variantPrice))}</span>`;

      // Badge de réduction
      if (variantReduction || (variantCompareAtPrice && parseInt(variantCompareAtPrice) > parseInt(variantPrice))) {
        let reduction;
        if (variantReduction) {
          reduction = `-${variantReduction}%`;
        } else {
          const reductionPercent = Math.round(
            ((parseInt(variantCompareAtPrice) - parseInt(variantPrice)) * 100) / parseInt(variantCompareAtPrice),
          );
          reduction = `-${reductionPercent}%`;
        }
        priceHTML += `<span class="badge badge--sale font-body-bold uppercase">Réduction ${reduction}</span>`;
      }

      summaryPriceContainer.innerHTML = priceHTML;
    }

    // Mettre à jour l'état du bouton d'ajout
    const addButton = product.querySelector('.bundle-popup__add-product-btn');
    if (addButton) {
      addButton.disabled = !variantAvailable;
    }

    // Mettre à jour l'option active dans le modal
    const modalBody = product.querySelector('.variant-picker-container__modal-body--options');
    if (modalBody) {
      // Supprimer la classe active de toutes les options
      modalBody.querySelectorAll('.variant-picker-container__modal-body--option').forEach((option) => {
        option.classList.remove('variant-picker-container__modal-body--option-selected');
      });

      // Ajouter la classe active à l'option sélectionnée
      const selectedLabel = input.closest('.variant-picker-container__modal-body--option');
      if (selectedLabel) {
        selectedLabel.classList.add('variant-picker-container__modal-body--option-selected');
      }
    }

    // Fermer le modal
    const details = input.closest('details');
    if (details) {
      details.open = false;
    }

    // Mettre à jour les produits sélectionnés si ce produit était déjà sélectionné
    if (this.selectedProducts.has(productId)) {
      const selectedProduct = this.selectedProducts.get(productId);

      selectedProduct.variantId = variantId;
      selectedProduct.price = parseFloat(variantPrice);
      this.selectedProducts.set(productId, selectedProduct);

      // Sauvegarder la nouvelle sélection de variante par productId
      this.userSelections.set(productId, {
        productId: productId,
        variantId: variantId,
        quantity: selectedProduct.quantity,
        price: parseFloat(variantPrice),
      });
      console.log(
        `Bundle Popup: Nouvelle variante sauvegardée - Produit ${productId}, Variante ${variantId}: ${selectedProduct.quantity}`,
      );

      // Mettre à jour les calculs
      this.updateProgress();
      this.updateAddButton();
    }

    console.log(`Bundle Popup: Variante changée pour le produit ${productId} -> ${variantId}`);
  }

  handleQuantityChange(event) {
    const btn = event.currentTarget;
    const product = btn.closest('.bundle-popup__product');
    const quantityValue = product.querySelector('.bundle-popup__quantity-value');
    const isMinus = btn.classList.contains('bundle-popup__quantity-btn--minus');

    let currentQuantity = parseInt(quantityValue.textContent) || 1;

    if (isMinus && currentQuantity > 0) {
      currentQuantity--;
    } else if (!isMinus) {
      currentQuantity++;
    }

    const productId = product.dataset.productId;
    const variantId = product.dataset.variantId;

    // Mettre à jour la quantité affichée
    quantityValue.textContent = currentQuantity;

    // Update selected products (même si quantité = 0 pour calculer les différences)
    if (productId) {
      const productData = {
        id: productId,
        variantId: variantId,
        quantity: currentQuantity,
        price: parseFloat(product.dataset.price),
      };

      this.selectedProducts.set(productId, productData);

      // Sauvegarder dans les sélections utilisateur persistantes par productId
      if (currentQuantity > 0) {
        this.userSelections.set(productId, {
          productId: productId,
          variantId: variantId,
          quantity: currentQuantity,
          price: parseFloat(product.dataset.price),
        });
        console.log(
          `Bundle Popup: Sélection sauvegardée - Produit ${productId}, Variante ${variantId}: ${currentQuantity}`,
        );
      } else {
        // Supprimer de la sélection si quantité = 0
        this.userSelections.delete(productId);
        console.log(`Bundle Popup: Sélection supprimée - Produit ${productId}`);
      }
    }

    // Si la quantité tombe à 0, transformer en bouton simple mais garder dans selectedProducts
    if (currentQuantity === 0) {
      this.transformToSimpleButton(product);
    }

    this.updateProgress();
    this.updateAddButton();
  }

  handleAddProduct(event) {
    const btn = event.currentTarget;
    const product = btn.closest('.bundle-popup__product');
    const productId = product.dataset.productId;
    const variantId = product.dataset.variantId;

    if (productId) {
      const productData = {
        id: productId,
        variantId: variantId,
        quantity: 1,
        price: parseFloat(product.dataset.price),
      };

      this.selectedProducts.set(productId, productData);

      // Sauvegarder dans les sélections utilisateur persistantes par productId
      this.userSelections.set(productId, productData);
      console.log(`Bundle Popup: Nouvelle sélection sauvegardée - Produit ${productId}, Variante ${variantId}: 1`);

      // Transformer le produit simple en produit avec contrôles
      this.transformToQuantityControls(product);

      this.updateProgress();
      this.updateAddButton();
    }
  }

  transformToQuantityControls(product) {
    // Vérifier si le produit a déjà des contrôles de quantité
    const existingQuantityControls = product.querySelector('.bundle-popup__product-quantity');
    if (existingQuantityControls) {
      // Mettre à jour la quantité à 1
      const quantityValue = existingQuantityControls.querySelector('.bundle-popup__quantity-value');
      if (quantityValue) {
        quantityValue.textContent = '1';
      }
      return;
    }

    // Remplacer le bouton + par des contrôles de quantité
    const actionsContainer = product.querySelector('.bundle-popup__product-actions');
    const addBtn = product.querySelector('.bundle-popup__add-product-btn');

    if (actionsContainer && addBtn) {
      // Créer les contrôles de quantité
      const quantityControls = document.createElement('div');
      quantityControls.className = 'bundle-popup__product-quantity';
      quantityControls.innerHTML = `
        <button class="bundle-popup__quantity-btn bundle-popup__quantity-btn--minus" type="button">
          <svg fill="none" viewBox="0 0 16 2" xmlns="http://www.w3.org/2000/svg">
            <path d="M14.3333 1H1" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
          </svg>
        </button>
        <span class="bundle-popup__quantity-value">1</span>
        <button class="bundle-popup__quantity-btn bundle-popup__quantity-btn--plus" type="button">
          <svg fill="none" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.66667 1V14.3333M1 7.66667H14.3333" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
          </svg>
        </button>
      `;

      // Remplacer le bouton + par les contrôles
      actionsContainer.replaceChild(quantityControls, addBtn);

      // Retirer la classe --simple du produit
      product.classList.remove('bundle-popup__product--simple');

      // Ajouter les event listeners aux nouveaux boutons
      const minusBtn = quantityControls.querySelector('.bundle-popup__quantity-btn--minus');
      const plusBtn = quantityControls.querySelector('.bundle-popup__quantity-btn--plus');

      if (minusBtn) minusBtn.addEventListener('click', this.handleQuantityChange);
      if (plusBtn) plusBtn.addEventListener('click', this.handleQuantityChange);

      console.log('Bundle Popup: Produit transformé en contrôles de quantité');
    }
  }

  transformToSimpleButton(product) {
    // Remplacer les contrôles de quantité par un bouton +
    const actionsContainer = product.querySelector('.bundle-popup__product-actions');
    const quantityControls = product.querySelector('.bundle-popup__product-quantity');

    if (actionsContainer && quantityControls) {
      // Créer le bouton +
      const addBtn = document.createElement('button');
      addBtn.className = 'bundle-popup__add-product-btn bundle-popup__add-product-btn--green';
      addBtn.type = 'button';
      addBtn.innerHTML = `
        <svg fill="none" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.66667 1V14.3333M1 7.66667H14.3333" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
        </svg>
      `;

      // Remplacer les contrôles par le bouton +
      actionsContainer.replaceChild(addBtn, quantityControls);

      // Ajouter la classe --simple au produit
      product.classList.add('bundle-popup__product--simple');

      // Ajouter l'event listener au nouveau bouton
      addBtn.addEventListener('click', this.handleAddProduct);

      console.log('Bundle Popup: Produit transformé en bouton simple');
    }
  }

  updateProgress() {
    // Calculer le total des quantités en évitant les doublons
    let totalQuantity = this.totalCartQuantity;

    // Pour les produits sélectionnés, ajouter seulement la différence avec ce qui est déjà dans le panier
    this.selectedProducts.forEach((product) => {
      const cartItem = this.cartItems.find((item) => item.variant_id.toString() === product.variantId);
      if (cartItem) {
        // Produit déjà dans le panier : ajouter seulement la différence
        const difference = product.quantity - cartItem.quantity;
        totalQuantity += difference;
      } else {
        // Nouveau produit : ajouter la quantité complète
        totalQuantity += product.quantity;
      }
    });

    const steps = this.querySelectorAll('.bundle-popup__progress-step');

    steps.forEach((step, index) => {
      const stepNumber = index + 2; // Steps start at 2 products
      const fill = step.querySelector('.bundle-popup__progress-fill');

      if (!fill) {
        console.warn('Bundle Popup: Progress fill element not found for step', stepNumber);
        return;
      }

      // Stocker l'état précédent pour détecter les changements
      const wasActive = fill.classList.contains('bundle-popup__progress-fill--active');
      const wasPartial = fill.classList.contains('bundle-popup__progress-fill--partial');

      // Reset all classes first
      fill.classList.remove('bundle-popup__progress-fill--active', 'bundle-popup__progress-fill--partial');

      if (totalQuantity >= stepNumber) {
        fill.classList.add('bundle-popup__progress-fill--active');
        console.log(`Bundle Popup: Step ${stepNumber} activated (total: ${totalQuantity})`);

        // Ajouter animation si c'est un nouveau changement
        if (!wasActive) {
          this.animateProgressFill(fill);
        }
      } else if (totalQuantity === stepNumber - 1) {
        fill.classList.add('bundle-popup__progress-fill--partial');
        console.log(`Bundle Popup: Step ${stepNumber} partial (total: ${totalQuantity})`);

        // Ajouter animation si c'est un nouveau changement
        if (!wasPartial) {
          this.animateProgressFill(fill);
        }
      } else {
        console.log(`Bundle Popup: Step ${stepNumber} inactive (total: ${totalQuantity})`);
      }
    });

    // Mettre à jour le texte de progression
    this.updateProgressText(totalQuantity);
  }

  updateProgressText(totalQuantity) {
    const progressText = this.querySelector('.bundle-popup__progress-text');
    if (!progressText) return;

    const messages = this.translations.progressMessages || {};

    let nextThreshold = 0;
    let nextDiscount = 0;

    if (totalQuantity < 2) {
      nextThreshold = 2;
      nextDiscount = 10;
    } else if (totalQuantity < 3) {
      nextThreshold = 3;
      nextDiscount = 15;
    } else if (totalQuantity < 4) {
      nextThreshold = 4;
      nextDiscount = 20;
    } else if (totalQuantity < 5) {
      nextThreshold = 5;
      nextDiscount = 25;
    } else {
      nextThreshold = 0; // Maximum reached
      nextDiscount = 25;
    }

    if (nextThreshold > 0) {
      const remaining = nextThreshold - totalQuantity;

      if (remaining === 1) {
        progressText.textContent = (
          messages.addOne || 'Ajouter 1 produit supplémentaire et obtenez {discount}% de réduction !'
        ).replace('{discount}', nextDiscount);
      } else {
        progressText.textContent = (
          messages.addMultiple || 'Ajouter {remaining} produits supplémentaires et obtenez {discount}% de réduction !'
        )
          .replace('{remaining}', remaining)
          .replace('{discount}', nextDiscount);
      }
    } else {
      progressText.textContent =
        messages.maxDiscount || 'Félicitations ! Vous bénéficiez de la réduction maximale de 25% !';
    }
  }

  async initializeProgress() {
    // Charger l'état du panier avant d'initialiser la progress bar
    console.log('Bundle Popup: Initializing progress bar');
    await this.loadCartState();
    this.updateProgress();
    this.updateAddButton();
  }

  animateProgressFill(fill) {
    // Ajouter la classe d'animation
    fill.classList.add('bundle-popup__progress-fill--animating');

    // Retirer la classe après l'animation
    setTimeout(() => {
      fill.classList.remove('bundle-popup__progress-fill--animating');
    }, 1000);
  }

  updateAddButton() {
    const addButton = this.querySelector('.bundle-popup__add-button');
    const quantityElement = this.querySelector('.bundle-popup__add-quantity');
    const discountElement = this.querySelector('.bundle-popup__add-discount');
    const addText = this.querySelector('[data-add-text]');
    const selectText = this.querySelector('[data-select-text]');

    if (!addButton) return;

    // Calculer la quantité totale finale
    let totalQuantity = this.totalCartQuantity;
    let discount = 0;

    // Ajuster pour les produits visibles modifiés
    this.selectedProducts.forEach((product) => {
      const cartItem = this.cartItems.find((item) => item.variant_id.toString() === product.variantId);

      if (cartItem) {
        // Produit déjà dans le panier : ajuster la quantité totale
        const difference = product.quantity - cartItem.quantity;
        totalQuantity += difference; // Ajouter la différence (peut être négative)
      } else {
        // Nouveau produit : ajouter quantité complète
        totalQuantity += product.quantity;
      }
    });

    // Calculate discount based on total quantity
    if (totalQuantity >= 5) discount = 25;
    else if (totalQuantity >= 4) discount = 20;
    else if (totalQuantity >= 3) discount = 15;
    else if (totalQuantity >= 2) discount = 10;

    if (quantityElement) {
      quantityElement.textContent = totalQuantity;
    }

    if (discountElement) {
      discountElement.textContent = discount > 0 ? `-${discount}%` : '';
      discountElement.style.display = discount > 0 ? 'flex' : 'none';
    }

    // Déterminer s'il y a des modifications ou si le panier a >= 2 produits
    const hasEnoughProducts = totalQuantity >= 2;

    // Nouvelle logique pour les textes du bouton
    if (addText && selectText) {
      if (hasEnoughProducts) {
        // "Valider ma routine"
        addText.style.display = 'inline';
        selectText.style.display = 'none';
        addButton.disabled = false;
        addButton.style.filter = '';
      } else {
        // "Sélectionner X soin(s)" selon le nombre manquant
        const needed = 2 - totalQuantity;
        const selectTranslations = this.translations.select || {};

        let selectLabel;
        if (needed === 1) {
          selectLabel = selectTranslations.one || 'Sélectionner 1 soin';
        } else {
          selectLabel = (selectTranslations.other || 'Sélectionner {{ count }} soins').replace('{{ count }}', needed);
        }

        addText.style.display = 'none';
        selectText.style.display = 'inline';
        selectText.textContent = selectLabel;
        addButton.disabled = true;
        addButton.style.filter = 'saturate(0)';
      }
    }

    console.log(
      `Bundle Popup: Bouton état - Total: ${totalQuantity}, Discount: ${discount}%, Texte: ${hasEnoughProducts ? 'Valider' : 'Sélectionner'}`,
    );
  }

  async handleAddToCart() {
    if (this.selectedProducts.size === 0) return;

    const addButton = this.querySelector('.bundle-popup__add-button');
    if (addButton) {
      addButton.disabled = true;
      addButton.classList.add('loading');
      addButton.querySelector('.loading__spinner').classList.remove('hidden');
    }

    try {
      // Analyser les actions à effectuer
      const updates = {};
      const additions = [];
      let hasUpdates = false;

      this.selectedProducts.forEach((product) => {
        const cartItem = this.cartItems.find((item) => item.variant_id.toString() === product.variantId);

        if (cartItem) {
          // Produit déjà dans le panier
          if (product.quantity !== cartItem.quantity) {
            updates[product.variantId] = product.quantity;
            hasUpdates = true;
            console.log(`Bundle Popup: Mise à jour ${product.variantId}: ${cartItem.quantity} -> ${product.quantity}`);
          }
        } else {
          // Nouveau produit à ajouter
          additions.push({
            id: product.variantId,
            quantity: product.quantity,
          });
          console.log(`Bundle Popup: Ajout ${product.variantId}: ${product.quantity}`);
        }
      });

      // Préparer les sections à mettre à jour comme dans product-form
      let sectionsToUpdate = {};
      if (this.cart) {
        sectionsToUpdate = {
          sections: this.cart.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        };
        this.cart.setActiveElement(document.activeElement);
      }

      // Si on a des mises à jour, utiliser cart/update.js
      if (hasUpdates) {
        console.log('Bundle Popup: Utilisation de cart/update.js', updates);

        console.log('[DEBUG] fetch cart/update.js', {
          url: window.routes.cart_update_url || '/cart/update.js',
          updates,
        });
        const updateResponse = await fetch(window.routes.cart_update_url || '/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
        });

        console.log('[DEBUG] cart/update.js status', updateResponse.status, updateResponse.statusText);

        const updateText = await updateResponse.text();
        console.log('[DEBUG] cart/update.js response text:', updateText);

        if (!updateResponse.ok) {
          throw new Error('Failed to update cart');
        }

        // cart/update.js ne retourne pas de JSON, juste un statut de succès
        console.log('Bundle Popup: Mise à jour du panier réussie');

        // Si on a aussi des ajouts, les faire après la mise à jour
        if (additions.length > 0) {
          console.log('Bundle Popup: Ajouts supplémentaires après mise à jour', additions);

          const addResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: additions,
              sections: sectionsToUpdate.sections,
            }),
          });

          if (!addResponse.ok) {
            throw new Error('Failed to add additional products to cart');
          }

          const addData = await addResponse.json();
        }
      } else if (additions.length > 0) {
        // Seulement des ajouts, utiliser cart/add.js classique
        console.log('Bundle Popup: Utilisation de cart/add.js classique', additions);

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: additions,
            sections: sectionsToUpdate.sections,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to add products to cart');
        }

        var addData = await response.json();
      }

      // Utiliser la réponse de /cart/add.js pour mettre à jour le panier et les sections
      let cartData = null;
      let sectionsData = null;

      if (typeof addData !== 'undefined') {
        cartData = addData.cart || addData; // selon la structure Shopify
        sectionsData = addData.sections || {};
      }

      // Mettre à jour l'état du panier local avec les données fraîches
      if (cartData) {
        this.updateCartFromData(cartData);
        // Publier l'événement de mise à jour du panier
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bundle-popup',
          cartData: cartData,
        });
      }

      // Rendre le contenu du panier si disponible
      if (this.cart) {
        if (sectionsToUpdate.sections && sectionsData) {
          // Créer un objet de données compatible avec renderContents
          const renderData = {
            ...cartData,
            sections: {},
          };

          sectionsToUpdate.sections.forEach((sectionId) => {
            if (sectionsData[sectionId]) {
              renderData.sections[sectionId] = sectionsData[sectionId];
            } else {
              console.error(`[DEBUG] bundle-popup.js: sectionId "${sectionId}" not found in sectionsData`);
            }
          });

          this.cart.renderContents(renderData);
        } else if (cartData) {
          // Fallback simple
          this.cart.renderContents(cartData);
        }
      }

      // Nettoyer les sélections utilisateur après ajout réussi
      this.clearUserSelections();

      this.close();

      // Show success message
      this.showSuccessMessage();
    } catch (error) {
      console.error('Error updating cart:', error);
      this.showErrorMessage();
    } finally {
      if (addButton) {
        addButton.disabled = false;
        addButton.classList.remove('loading');
        addButton.querySelector('.loading__spinner').classList.add('hidden');

        // Restaurer le contenu du bouton et mettre à jour l'état
        this.updateAddButton();
      }
    }
  }

  handleSkip() {
    this.close();
  }

  handleClose() {
    this.close();
  }

  handleOverlayClick(event) {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  handleSearch(event) {
    const query = event.target.value.trim();

    if (query.length === 0) {
      // Si la recherche est vide, afficher les produits recommandés
      this.showRecommendedProducts();
      return;
    }

    if (query.length < 2) {
      // Attendre au moins 2 caractères pour lancer la recherche
      return;
    }

    // Utiliser la recherche prédictive avec debounce
    this.debouncedSearch(query);
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  performPredictiveSearch(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();

    if (this.cachedResults[queryKey]) {
      // Utiliser requestAnimationFrame pour les manipulations DOM
      requestAnimationFrame(() => {
        this.renderSearchResults(this.cachedResults[queryKey]);
      });
      return;
    }

    // Annuler la requête précédente pour éviter les race conditions
    this.abortController.abort();
    this.abortController = new AbortController();

    // Utiliser la même URL que predictive-search mais avec notre section (limite augmentée pour filtrage)
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=bundle-popup&resources[type]=product&resources[limit]=20`,
      {
        signal: this.abortController.signal,
      },
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        // Optimiser le parsing DOM avec requestAnimationFrame
        requestAnimationFrame(() => {
          const resultsMarkup = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('.bundle-popup__products').innerHTML;

          // Mettre en cache le résultat
          this.cachedResults[queryKey] = resultsMarkup;

          this.renderSearchResults(resultsMarkup);
        });
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Erreur lors de la recherche prédictive:', error);
          this.showSearchError();
        }
      });
  }

  renderSearchResults(resultsMarkup) {
    const productsContainer = this.querySelector('.bundle-popup__products');
    if (productsContainer) {
      productsContainer.innerHTML = resultsMarkup;

      // Filtrer les produits pack après insertion des résultats de recherche
      const productsList = productsContainer.querySelector('.bundle-popup__products-list');
      //this.filterPackProducts(productsList);

      // Réattacher les event listeners aux nouveaux produits
      this.attachProductEventListeners();

      // Synchroniser avec le panier d'abord
      this.syncProductsWithCart();

      // Puis restaurer les sélections utilisateur (priorité sur le panier)
      this.restoreUserSelections();

      this.updateProgress();
      this.updateAddButton();
    }
  }

  async showRecommendedProducts() {
    // Recharger les produits recommandés originaux
    await this.loadBundleRecommendations(this.currentProductId);
  }

  showSearchError() {
    const productsContainer = this.querySelector('.bundle-popup__products');
    if (productsContainer) {
      productsContainer.innerHTML = `
        <div class="bundle-popup__search-error">
          <p>Une erreur s'est produite lors de la recherche. Veuillez réessayer.</p>
        </div>
      `;
    }
  }

  attachProductEventListeners() {
    // Réattacher les event listeners pour les boutons d'ajout et de quantité
    const addButtons = this.querySelectorAll('.bundle-popup__add-product-btn');
    addButtons.forEach((btn) => {
      btn.addEventListener('click', this.handleAddProduct);
    });

    const quantityButtons = this.querySelectorAll('.bundle-popup__quantity-btn');
    quantityButtons.forEach((btn) => {
      btn.addEventListener('click', this.handleQuantityChange);
    });

    // Réattacher les event listeners pour les variantes
    const variantInputs = this.querySelectorAll('.js-variant-picker input[type="radio"]');
    variantInputs.forEach((input) => {
      input.addEventListener('change', this.handleVariantChange.bind(this));
    });
  }

  restoreUserSelections() {
    // Restaurer les sélections utilisateur après un rendu de produits
    console.log(`Bundle Popup: Restauration de ${this.userSelections.size} sélections utilisateur`);

    this.userSelections.forEach((selection, productId) => {
      const product = this.querySelector(`[data-product-id="${productId}"]`);

      if (product) {
        const storedVariantId = selection.variantId;
        const currentVariantId = product.dataset.variantId;

        // Restaurer dans selectedProducts
        this.selectedProducts.set(productId, {
          id: productId,
          variantId: storedVariantId,
          quantity: selection.quantity,
          price: selection.price,
        });

        // Transformer en contrôles de quantité et mettre à jour la quantité
        this.transformToQuantityControls(product);

        const quantityValue = product.querySelector('.bundle-popup__quantity-value');
        if (quantityValue) {
          quantityValue.textContent = selection.quantity;
        }

        // Mettre à jour le prix affiché si nécessaire
        const priceContainer = product.querySelector('[data-product-price]');
        if (priceContainer) {
          const priceSpan = priceContainer.querySelector('.price__current');
          if (priceSpan) {
            priceSpan.textContent = this.formatPrice(selection.price);
          }
        }

        // Vérifier si la variante affichée correspond à celle stockée
        const variantInput = product.querySelector(`input[value="${storedVariantId}"]`);

        if (currentVariantId !== storedVariantId) {
          // La variante affichée ne correspond pas à celle stockée, la corriger
          console.log(
            `Bundle Popup: Correction de variante - Produit ${productId}, Affiché: ${currentVariantId}, Stocké: ${storedVariantId}`,
          );

          // Mettre à jour les données du produit
          product.dataset.variantId = storedVariantId;
          product.dataset.price = variantInput?.dataset.variantPrice || selection.price;
        }

        if (variantInput) {
          // Cocher la bonne variante
          if (!variantInput.checked) {
            variantInput.checked = true;
          }

          // Mettre à jour l'option active dans le modal
          const modalBody = product.querySelector('.variant-picker-container__modal-body--options');
          if (modalBody) {
            // Supprimer la classe active de toutes les options
            modalBody.querySelectorAll('.variant-picker-container__modal-body--option').forEach((option) => {
              option.classList.remove('variant-picker-container__modal-body--option-selected');
            });

            // Ajouter la classe active à l'option sélectionnée
            const selectedLabel = variantInput.closest('.variant-picker-container__modal-body--option');
            if (selectedLabel) {
              selectedLabel.classList.add('variant-picker-container__modal-body--option-selected');
            }
          }

          // Mettre à jour le texte sélectionné dans le summary
          const selectedText = product.querySelector('[data-selected-value]');
          if (selectedText) {
            const optionTitle = variantInput
              .closest('label')
              ?.querySelector('.variant-picker-container__modal-body--option-title');
            if (optionTitle) {
              selectedText.textContent = optionTitle.textContent;
            }
          }

          // Mettre à jour le prix dans le summary du variant picker
          const summaryPriceContainer = product.querySelector('.variant-picker-container__selected-variant--price');
          if (summaryPriceContainer) {
            const variantPrice = variantInput.dataset.variantPrice;
            const variantCompareAtPrice = variantInput.dataset.variantCompareAtPrice;
            const variantReduction = variantInput.dataset.variantReduction;

            let priceHTML = '';

            // Prix barré si compare_at_price existe
            if (variantCompareAtPrice && parseInt(variantCompareAtPrice) > parseInt(variantPrice)) {
              priceHTML += `<s>${this.formatPrice(parseInt(variantCompareAtPrice))}</s>`;
            }

            // Prix actuel
            priceHTML += `<span>${this.formatPrice(parseInt(variantPrice))}</span>`;

            // Badge de réduction
            if (
              variantReduction ||
              (variantCompareAtPrice && parseInt(variantCompareAtPrice) > parseInt(variantPrice))
            ) {
              let reduction;
              if (variantReduction) {
                reduction = `-${variantReduction}%`;
              } else {
                const reductionPercent = Math.round(
                  ((parseInt(variantCompareAtPrice) - parseInt(variantPrice)) * 100) / parseInt(variantCompareAtPrice),
                );
                reduction = `-${reductionPercent}%`;
              }
              priceHTML += `<span class="badge badge--sale font-body-bold uppercase">Réduction ${reduction}</span>`;
            }

            summaryPriceContainer.innerHTML = priceHTML;
          }
        }

        console.log(
          `Bundle Popup: Sélection restaurée - Produit ${productId}, Variante ${storedVariantId}: ${selection.quantity}`,
        );
      } else {
        console.log(`Bundle Popup: Produit ${productId} non trouvé dans les résultats actuels`);
      }
    });
  }

  async open() {
    console.debug('[DEBUG] bundle-popup: open() called');
    this.isOpen = true;
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Mettre à jour les informations du panier à chaque ouverture
    await this.loadCartState();
    this.syncProductsWithCart();
    this.updateProgress();
    this.updateAddButton();

    // Focus management
    const firstFocusable = this.querySelector('.bundle-popup__close');
    if (firstFocusable) {
      firstFocusable.focus();
    }
    console.debug('[DEBUG] bundle-popup: open() completed');
  }

  updateCartFromData(cartData) {
    try {
      // Mettre à jour les données du panier depuis l'événement cart:updated
      if (cartData && cartData.items) {
        // Filtrer les items pour exclure les packs et les produits avec "Feuille de soins"
        const filteredItems = cartData.items.filter((item) => {
          const isNotPack = item.product_type !== 'pack';
          const hasNoFeuilleDesoins = !item.properties || !item.properties['Feuille de soins'];
          return isNotPack && hasNoFeuilleDesoins;
        });

        this.cartItems = filteredItems;
        this.totalCartQuantity = filteredItems.reduce((total, item) => total + item.quantity, 0);
        console.log(
          `Bundle Popup: ${this.totalCartQuantity} articles valides dans le panier (${cartData.items.length - filteredItems.length} items filtrés)`,
        );
      } else if (cartData && Array.isArray(cartData)) {
        // Si cartData est directement un array d'items
        const filteredItems = cartData.filter((item) => {
          const isNotPack = item.product_type !== 'pack';
          const hasNoFeuilleDesoins = !item.properties || !item.properties['Feuille de soins'];
          return isNotPack && hasNoFeuilleDesoins;
        });

        this.cartItems = filteredItems;
        this.totalCartQuantity = filteredItems.reduce((total, item) => total + item.quantity, 0);
        console.log(
          `Bundle Popup: ${this.totalCartQuantity} articles valides dans le panier (${cartData.length - filteredItems.length} items filtrés)`,
        );
      } else {
        this.cartItems = [];
        this.totalCartQuantity = 0;
      }

      // Mettre à jour l'interface si le popup est ouvert
      if (this.isOpen) {
        this.updateProgress();
        this.updateAddButton();
      }
    } catch (error) {
      console.error("Bundle Popup: Erreur lors de la mise à jour du panier depuis l'événement:", error);
    }
  }

  async loadCartState() {
    try {
      // Récupérer l'état actuel du panier via API (fallback)
      const response = await fetch('/cart.js');
      const cart = await response.json();

      if (cart && cart.items) {
        // Filtrer les items pour exclure les packs et les produits avec "Feuille de soins"
        const filteredItems = cart.items.filter((item) => {
          const isNotPack = item.product_type !== 'pack';
          const hasNoFeuilleDesoins = !item.properties || !item.properties['Feuille de soins'];
          return isNotPack && hasNoFeuilleDesoins;
        });

        this.cartItems = filteredItems;
        this.totalCartQuantity = filteredItems.reduce((total, item) => total + item.quantity, 0);
        console.log(
          `Bundle Popup: ${this.totalCartQuantity} articles valides dans le panier (${cart.items.length - filteredItems.length} items filtrés)`,
        );
      } else {
        this.cartItems = [];
        this.totalCartQuantity = 0;
      }
    } catch (error) {
      console.error('Bundle Popup: Erreur lors de la récupération du panier:', error);
      this.cartItems = [];
      this.totalCartQuantity = 0;
    }
  }

  syncProductsWithCart() {
    // NE PAS réinitialiser selectedProducts car cela efface les sélections utilisateur
    // this.selectedProducts.clear(); // SUPPRIMÉ

    // Synchroniser les quantités des produits avec celles du panier
    const products = this.querySelectorAll('.bundle-popup__product');

    products.forEach((product) => {
      const variantId = product.dataset.variantId;
      if (!variantId) return;

      // Vérifier d'abord si ce produit a une sélection utilisateur par productId
      const productId = product.dataset.productId;
      const userSelection = this.userSelections.get(productId);

      // Chercher ce variant dans le panier
      const cartItem = this.cartItems.find((item) => item.variant_id.toString() === variantId);

      if (userSelection) {
        // Priorité aux sélections utilisateur
        console.log(
          `Bundle Popup: Utilisation de la sélection utilisateur pour ${variantId}: ${userSelection.quantity}`,
        );

        // Déjà géré par restoreUserSelections, ne pas écraser
        return;
      } else if (cartItem) {
        // Le produit est dans le panier mais pas de sélection utilisateur
        const existingQuantity = cartItem.quantity;

        // Transformer en contrôles de quantité si ce n'est pas déjà fait
        this.transformToQuantityControls(product);

        // Mettre à jour la quantité affichée
        const quantityValue = product.querySelector('.bundle-popup__quantity-value');
        if (quantityValue) {
          quantityValue.textContent = existingQuantity;
        }

        // Ajouter aux produits sélectionnés avec la quantité du panier
        const productId = product.dataset.productId;
        if (productId) {
          this.selectedProducts.set(productId, {
            id: productId,
            variantId: variantId,
            quantity: existingQuantity,
            price: parseFloat(product.dataset.price),
          });
        }

        console.log(`Bundle Popup: Produit ${productId} synchronisé avec ${existingQuantity} exemplaires du panier`);
      } else {
        // Le produit n'est pas dans le panier et pas de sélection utilisateur
        this.transformToSimpleButton(product);
      }
    });

    console.log(`Bundle Popup: ${this.selectedProducts.size} produits synchronisés (panier + sélections utilisateur)`);
  }

  close() {
    console.log('Bundle Popup: Fermeture');
    this.isOpen = false;
    this.classList.remove('is-open');
    document.body.style.overflow = '';

    // Reset state (mais conserver userSelections pour la persistance)
    this.selectedProducts.clear();
    this.currentProductId = null;

    console.log(`Bundle Popup: Fermeture - ${this.userSelections.size} sélections utilisateur conservées`);
  }

  clearUserSelections() {
    // Nettoyer complètement les sélections utilisateur (utilisé après ajout au panier réussi)
    this.userSelections.clear();
    console.log('Bundle Popup: Toutes les sélections utilisateur ont été effacées');
  }

  initializeScrollbar() {
    const productsList = this.querySelector('.bundle-popup__products-list');
    const scrollbarThumb = this.querySelector('.bundle-popup__scrollbar-thumb');

    if (!productsList || !scrollbarThumb) return;

    const updateScrollbar = () => {
      const scrollRatio = productsList.scrollTop / (productsList.scrollHeight - productsList.clientHeight);
      const thumbHeight = (productsList.clientHeight / productsList.scrollHeight) * 100;
      const thumbPosition = scrollRatio * (100 - thumbHeight);

      scrollbarThumb.style.height = `${Math.max(thumbHeight, 10)}%`;
      scrollbarThumb.style.transform = `translateY(${thumbPosition}%)`;
    };

    productsList.addEventListener('scroll', updateScrollbar);
    updateScrollbar();
  }

  formatPrice(price) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(price / 100);
  }

  showSuccessMessage() {
    // You can implement a toast notification here
    console.log('Products added to cart successfully!');
  }

  showErrorMessage() {
    // You can implement a toast notification here
    console.error('Failed to add products to cart');
  }
}

customElements.define('bundle-popup', BundlePopup);
